import { createSelector } from '@reduxjs/toolkit';

import { SearchBoxDialogName } from 'Components/names';
import {
  sendAutocompleteRequest,
  setAutocompleteLoading,
  setAutocompleteQuery,
  setAutocompleteResponse,
  setDefaultAutocompleteSuggestions,
} from 'Core/actions/autocomplete';
import { dialogOpened } from 'Core/actions/dialog';
import type { EpicArg } from 'Core/epics/common';
import { defaultAutocompleteResponseState } from 'Core/reducers/autocomplete/response';
import {
  autocompleteDefaultSuggestionsSelector,
  autocompleteRequestQuerySelector,
  autocompleteResponseItemsSelector,
} from 'Core/selectors/autocomplete';
import { autocompletePreselectionSelector } from 'Core/selectors/preselection';
import { AutocompleteItem, CustomError, CustomErrorType, FacetValueBase } from 'Models/index';
import { abortControllerCreator, autocomplete } from 'Modules/serverApi/index';
import type { ServerModel } from 'Modules/serverApi/types';

const requestController = abortControllerCreator();

const isRedirectItem = (item: ServerModel.AutocompleteItem): item is ServerModel.AutocompleteProductItem =>
  item.Type === 'Product';

const requestSelector = createSelector(
  autocompleteRequestQuerySelector,
  autocompletePreselectionSelector,
  (query: string, selection: FacetValueBase[]) => ({ query, selection }),
);

export default async function ({ dispatch, state, action: { payload, type } }: EpicArg): Promise<void> {
  const isInputTrigged = type === setAutocompleteQuery.type;
  const isActionTrigged = type === sendAutocompleteRequest.type;

  const isDialogJustOpened = type === dialogOpened.type && payload === SearchBoxDialogName;
  const autocompleteResponseItems = autocompleteResponseItemsSelector(state) ?? [];
  const isDialogTriggered = isDialogJustOpened && !autocompleteResponseItems.length;

  if (!isInputTrigged && !isActionTrigged && !isDialogTriggered) {
    return;
  }

  const request = isActionTrigged ? payload : requestSelector(state);

  const isDefaultSuggestionsRequest = !request.query;
  const defaultSuggestion = autocompleteDefaultSuggestionsSelector(state);
  const allowToDefaultSuggestionRequest = isDefaultSuggestionsRequest && !defaultSuggestion;

  const selection = allowToDefaultSuggestionRequest ? { query: request.query } : request;

  if (allowToDefaultSuggestionRequest || request.query.length >= 3) {
    const isFilterWithinAutocomplete = isActionTrigged && !!request.query;

    try {
      dispatch(setAutocompleteLoading(true));

      const rawResponse = await autocomplete({ ...selection }, requestController.create());

      const responseItems = rawResponse.map((item) => new AutocompleteItem(item));

      if (isDefaultSuggestionsRequest) {
        dispatch(setDefaultAutocompleteSuggestions(responseItems));
      } else {
        // if performed filter within autocomplete, then update only product, do not update text or catagory suggestions
        // TODO: send 'type' in request to get only product, we don't need other types
        const items = isFilterWithinAutocomplete
          ? autocompleteResponseItems
              .filter((i) => i.type !== 'product')
              .concat(responseItems?.filter((i) => i.type === 'product') ?? [])
          : responseItems;

        const { TotalHits: totalHits = 0 } = rawResponse.find(isRedirectItem) || {};

        dispatch(setAutocompleteLoading(false));
        dispatch(setAutocompleteResponse({ items, totalHits, query: request.query }));
      }
    } catch (err: CustomError | unknown) {
      if (err instanceof CustomError && err.type !== CustomErrorType.requestAborted) {
        dispatch(setAutocompleteLoading(false));
        dispatch(setAutocompleteResponse(defaultAutocompleteResponseState));
      }
    }
  }
}
